
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { Toaster } from "@/components/ui/sonner";
import { initializeOptimizations } from '@/utils/optimizeImages';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Apply performance optimizations when the layout mounts
  useEffect(() => {
    if (!isLoaded) {
      // Initialize essential optimizations immediately
      initializeOptimizations();
      setIsLoaded(true);
      
      // Track performance metrics
      if (window.performance && 'PerformanceObserver' in window) {
        // Create a performance observer to monitor loading metrics
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.entryType === 'largest-contentful-paint') {
              console.log('LCP:', entry.startTime);
            }
          });
        });
        
        // Observe LCP performance entries
        observer.observe({ type: 'largest-contentful-paint', buffered: true });
        
        return () => {
          observer.disconnect();
        };
      }
    }
  }, [isLoaded]);
  
  // Scroll to top when the route changes, but only after initial load
  useEffect(() => {
    if (isLoaded) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      
      // Optionally prefetch resources for the new page
      if (location.hash === '#top') {
        // If navigating to the top of a page, preload critical resources
        const pagePath = location.pathname;
        if (pagePath.includes('/klaverundervisning-')) {
          // Preload location-specific images
          const locationImage = document.querySelector('img[alt*="Klaverundervisning"]') as HTMLImageElement;
          if (locationImage && locationImage.src) {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = locationImage.src;
            document.head.appendChild(link);
          }
        }
      }
    }
  }, [location.pathname, isLoaded]);

  return (
    <div className="flex flex-col min-h-screen">
      <Toaster position="top-right" />
      <Header />
      <main className="flex-grow">{children}</main>
      <Footer />
    </div>
  );
};

// Export memoized version to prevent unnecessary re-renders
export default React.memo(Layout);
